import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Heart, Frown, Meh, Smile, Crosshair, CheckCircle2 } from "lucide-react";

/**
 * Página pública de respuesta NPS. NO requiere login.
 * URL: /nps/{token}
 */
export default function NPSRespond() {
    const { token } = useParams();
    const [survey, setSurvey] = useState(null);
    const [error, setError] = useState(null);
    const [score, setScore] = useState(null);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/nps/${token}`);
                setSurvey(data);
                if (data.responded_at) setDone(true);
            } catch (e) {
                setError(e?.response?.data?.detail || "Encuesta no encontrada");
            }
        })();
    }, [token]);

    const submit = async () => {
        if (score === null) return;
        setSubmitting(true);
        try {
            await api.post(`/nps/${token}/respond`, { score, comment });
            setDone(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Error al enviar respuesta");
        } finally {
            setSubmitting(false);
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-sm shadow max-w-md text-center" data-testid="nps-error">
                    <Frown className="w-12 h-12 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
                    <h1 className="font-display font-bold text-lg">{error}</h1>
                </div>
            </div>
        );
    }

    if (!survey) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Cargando...</div>;

    if (done) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
                <div className="bg-white p-10 rounded-sm shadow max-w-md text-center" data-testid="nps-thanks">
                    <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" strokeWidth={1.5} />
                    <div className="text-[10px] uppercase tracking-widest text-red-600 font-bold">FINDMED · GRACIAS</div>
                    <h1 className="font-display font-black text-2xl mt-2">Tu respuesta ha sido registrada</h1>
                    <p className="text-sm text-slate-500 mt-3">
                        Apreciamos profundamente que hayas tomado un momento para evaluarnos.
                        Tu retroalimentación nos ayuda a brindar mejor servicio.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="nps-page">
            <div className="bg-white rounded-sm shadow-lg max-w-2xl w-full overflow-hidden">
                <div className="bg-slate-900 text-white p-6 border-b-4 border-red-600">
                    <div className="flex items-center gap-2 mb-2">
                        <Crosshair className="w-4 h-4 text-red-400" />
                        <span className="text-[10px] uppercase tracking-widest font-bold text-red-400">FINDMED · ENCUESTA DE CALIDAD</span>
                    </div>
                    <h1 className="font-display font-black text-2xl">¿Qué tan probable es que nos recomiendes?</h1>
                    <div className="text-xs text-slate-300 mt-2 font-mono">
                        Servicio folio {survey.service_folio} · {survey.patient_name}
                    </div>
                </div>

                <div className="p-6 space-y-5">
                    <p className="text-sm text-slate-700">
                        <strong>{survey.client_name},</strong> califica del 0 al 10 qué tan probable es que recomiendes
                        FindMed Ambulancias a un colega, familiar o amigo.
                    </p>

                    <div className="grid grid-cols-11 gap-1.5" data-testid="score-grid">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                            const isPromoter = n >= 9;
                            const isPassive = n >= 7 && n <= 8;
                            const sel = score === n;
                            const baseColor = isPromoter ? "bg-emerald-500" : isPassive ? "bg-amber-500" : "bg-red-500";
                            return (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setScore(n)}
                                    data-testid={`nps-score-${n}`}
                                    className={`aspect-square rounded-sm text-base font-bold transition-all ${sel ? `${baseColor} text-white scale-110 shadow-md` : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}
                                >
                                    {n}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-slate-400">
                        <span><Frown className="w-3 h-3 inline" /> No recomendaría</span>
                        <span><Meh className="w-3 h-3 inline" /> Neutral</span>
                        <span>Recomendaría 100% <Smile className="w-3 h-3 inline" /></span>
                    </div>

                    <div>
                        <label className="fm-label">Comentario (opcional)</label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Cuéntanos qué hicimos bien y qué podemos mejorar..."
                            className="fm-input"
                            rows={4}
                            maxLength={1000}
                            data-testid="comment-input"
                        />
                    </div>

                    <button
                        onClick={submit}
                        disabled={submitting || score === null}
                        data-testid="nps-submit"
                        className="w-full bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 rounded-sm font-bold text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2"
                    >
                        <Heart className="w-4 h-4" /> {submitting ? "Enviando..." : "Enviar mi respuesta"}
                    </button>
                </div>

                <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 text-[10px] uppercase tracking-widest font-bold text-slate-400 font-mono text-center">
                    FindMed Dispatch Control · Encuesta de calidad anónima
                </div>
            </div>
        </div>
    );
}
